import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { statusService } from '../../services/statusService';
import StatusText from './StatusText';

const EDIT_WINDOW_MS = 72 * 60 * 60 * 1000;
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const parseDisplayDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && DATE_ONLY_REGEX.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return { date: new Date(year, month - 1, day), hasTime: false };
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return { date, hasTime: true };
};

const getCreatedAtValue = (update) => update?.createdAt || update?.created_at || null;

const getUpdateDateValue = (update) => {
  const createdAtValue = getCreatedAtValue(update);
  if (parseDisplayDate(createdAtValue)) return createdAtValue;
  return update?.date;
};

const formatDateTime = (value) => {
  const parsed = parseDisplayDate(value);
  if (!parsed) return value || '-';

  if (!parsed.hasTime) {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    }).format(parsed.date);
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(parsed.date);
};

const formatTimeOnly = (value) => {
  const parsed = parseDisplayDate(value);
  if (!parsed || !parsed.hasTime) return '-';
  return new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit' }).format(parsed.date);
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'Completed': return 'bg-green-100 text-green-800';
    case 'Blocked': return 'bg-red-100 text-red-800';
    case 'In Progress': return 'bg-blue-100 text-blue-800';
    case 'In Review': return 'bg-indigo-100 text-indigo-800';
    case 'On Hold': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getPriorityBadgeClass = (priority) => {
  switch (priority) {
    case 'P1': return 'bg-red-100 text-red-800';
    case 'P2': return 'bg-orange-100 text-orange-800';
    case 'P3': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-green-100 text-green-800';
  }
};

const getSummaryText = (value) => {
  if (!value) return '-';
  return value.replace(/\n\nExternal Link: https?:\/\/[^\s]+/, '').trim();
};

const extractExternalLink = (value) => {
  if (!value) return null;
  const linkMatch = value.match(/External Link: (https?:\/\/[^\s]+)/);
  return linkMatch ? linkMatch[1] : null;
};

const isWithinPreset = (dateValue, preset) => {
  if (preset === 'all') return true;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;

  const now = new Date();
  const day = now.getDay();
  const diffToMonday = day === 0 ? 6 : day - 1;
  const thisWeekStart = new Date(now);
  thisWeekStart.setHours(0, 0, 0, 0);
  thisWeekStart.setDate(now.getDate() - diffToMonday);
  const thisWeekEnd = new Date(thisWeekStart);
  thisWeekEnd.setDate(thisWeekStart.getDate() + 7);

  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(thisWeekStart.getDate() - 7);

  if (preset === 'thisWeek') return date >= thisWeekStart && date < thisWeekEnd;
  if (preset === 'lastWeek') return date >= lastWeekStart && date < thisWeekStart;
  return true;
};

const getEditMeta = (update, currentUserId) => {
  const posterId = update.createdById || update.user?.id || update.userId || update.user_id;
  const isPoster = String(posterId) === String(currentUserId);
  const createdAtValue = getCreatedAtValue(update) || update.date;
  const createdAtMs = new Date(createdAtValue).getTime();
  const cutoffAt = Number.isNaN(createdAtMs) ? null : new Date(createdAtMs + EDIT_WINDOW_MS);
  const withinWindow = cutoffAt ? Date.now() <= cutoffAt.getTime() : false;
  const canEdit = isPoster && withinWindow;
  const reason = isPoster ? 'Edit window expired (3 days).' : 'Editable for 3 days after posting.';
  return { canEdit, reason, isPoster, withinWindow };
};

const StatusUpdatesTable = ({ updates, showUserColumn = false }) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [quickPreset, setQuickPreset] = useState('all');
  const [tableUpdates, setTableUpdates] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [savingRowId, setSavingRowId] = useState(null);
  const [rowMessages, setRowMessages] = useState({});

  useEffect(() => {
    setTableUpdates(updates || []);
  }, [updates]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const rows = (tableUpdates || []).filter((update) => {
      const dateValue = getUpdateDateValue(update);
      if (!isWithinPreset(dateValue, quickPreset)) return false;
      if (!term) return true;
      const haystack = [
        update.task?.title || '',
        update.user?.name || '',
        update.statusText || '',
        update.status || '',
        update.task?.priority || ''
      ].join(' ').toLowerCase();
      return haystack.includes(term);
    });

    rows.sort((a, b) => {
      if (sortConfig.key === 'hours') {
        const aHours = Number(a.timeSpentHours || 0);
        const bHours = Number(b.timeSpentHours || 0);
        return sortConfig.direction === 'asc' ? aHours - bHours : bHours - aHours;
      }
      const aDate = new Date(getUpdateDateValue(a) || 0).getTime();
      const bDate = new Date(getUpdateDateValue(b) || 0).getTime();
      return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
    });

    return rows;
  }, [tableUpdates, searchTerm, quickPreset, sortConfig]);

  const setSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      return { key, direction: key === 'date' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  const startEdit = (update) => {
    setEditingRowId(update.id);
    setExpandedRowId(update.id);
    setRowMessages(prev => ({ ...prev, [update.id]: '' }));
    setEditDraft({
      date: update.date || '',
      timeSpentHours: update.timeSpentHours ?? '',
      status: update.status || 'Not Started',
      statusText: getSummaryText(update.statusText)
    });
  };

  const saveEdit = async (update) => {
    if (!editDraft) return;
    setSavingRowId(update.id);
    setRowMessages(prev => ({ ...prev, [update.id]: '' }));
    try {
      const response = await statusService.updateStatusUpdate(update.id, {
        date: editDraft.date,
        status: editDraft.status,
        statusText: editDraft.statusText,
        timeSpentHours: Number(editDraft.timeSpentHours)
      });
      const updated = response?.data;
      setTableUpdates(prev => prev.map(item => (item.id === update.id ? updated : item)));
      setEditingRowId(null);
      setEditDraft(null);
      setRowMessages(prev => ({ ...prev, [update.id]: 'Status update edited successfully.' }));
    } catch (error) {
      setRowMessages(prev => ({
        ...prev,
        [update.id]: error.response?.data?.message || 'Failed to edit status update.'
      }));
    } finally {
      setSavingRowId(null);
    }
  };

  if (!updates || updates.length === 0) {
    return <div className="bg-white rounded-lg shadow-md p-10 text-center text-gray-500">No status updates found for this range.</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setQuickPreset('all')} className={`px-3 py-1.5 text-sm rounded border ${quickPreset === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50'}`}>All</button>
          <button type="button" onClick={() => setQuickPreset('thisWeek')} className={`px-3 py-1.5 text-sm rounded border ${quickPreset === 'thisWeek' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50'}`}>This Week</button>
          <button type="button" onClick={() => setQuickPreset('lastWeek')} className={`px-3 py-1.5 text-sm rounded border ${quickPreset === 'lastWeek' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50'}`}>Last Week</button>
        </div>
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search task, user, update text..." className="w-full md:w-80 border rounded px-3 py-2 text-sm" aria-label="Search status updates" />
      </div>

      <div className="overflow-auto max-h-[68vh]">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"><button type="button" onClick={() => setSort('date')} className="inline-flex items-center gap-1 hover:text-gray-700">Date/Time {sortIndicator('date')}</button></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Task</th>
              {showUserColumn && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">User</th>}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Update Summary</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"><button type="button" onClick={() => setSort('hours')} className="inline-flex items-center gap-1 hover:text-gray-700">Hours {sortIndicator('hours')}</button></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Status/Tags</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Details</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((update, index) => {
              const isExpanded = expandedRowId === update.id;
              const isEditing = editingRowId === update.id;
              const summary = getSummaryText(update.statusText);
              const detailId = `status-detail-${update.id}`;
              const editMeta = getEditMeta(update, user?.id);
              const externalLink = extractExternalLink(update.statusText);

              return (
                <React.Fragment key={update.id}>
                  <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDateTime(getUpdateDateValue(update))}</td>
                    <td className="px-4 py-3">
                      {update.task?.id ? <Link to={`/tasks/${update.task.id}`} className="text-sm font-medium text-primary hover:underline">{update.task?.title || '-'}</Link> : <span className="text-sm font-medium text-gray-800">{update.task?.title || '-'}</span>}
                    </td>
                    {showUserColumn && <td className="px-4 py-3 text-sm text-gray-700 hidden lg:table-cell">{update.user?.name || '-'}</td>}
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-lg hidden md:table-cell"><p className="truncate">{summary || '-'}</p></td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{Number(update.timeSpentHours || 0).toFixed(1)}h</td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex w-[110px] justify-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(update.status)}`}>{update.status || 'Unknown'}</span>
                        <span className={`inline-flex w-[72px] justify-center px-2.5 py-1 rounded-full text-xs font-semibold ${getPriorityBadgeClass(update.task?.priority)}`}>{update.task?.priority || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setExpandedRowId(isExpanded ? null : update.id)} className="text-sm text-primary hover:underline" aria-expanded={isExpanded} aria-controls={detailId}>
                        {isExpanded ? 'Hide' : 'View'}
                      </button>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {editMeta.canEdit ? (
                        <button type="button" onClick={() => startEdit(update)} className="text-sm text-blue-700 hover:underline">Edit</button>
                      ) : (
                        <span className="text-xs text-gray-500" title={editMeta.reason}>{editMeta.reason}</span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr id={detailId} className="bg-white">
                      <td className="px-4 py-4 border-t border-gray-100" colSpan={showUserColumn ? 8 : 7}>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 text-sm">
                          <div className="lg:col-span-2">
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Full Update</p>
                            {isEditing ? (
                              <div className="space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <input type="date" value={editDraft?.date || ''} onChange={(e) => setEditDraft(prev => ({ ...prev, date: e.target.value }))} className="border rounded px-3 py-2" />
                                  <input type="number" min="0" max="16" step="0.5" value={editDraft?.timeSpentHours ?? ''} onChange={(e) => setEditDraft(prev => ({ ...prev, timeSpentHours: e.target.value }))} className="border rounded px-3 py-2" />
                                  <select value={editDraft?.status || 'Not Started'} onChange={(e) => setEditDraft(prev => ({ ...prev, status: e.target.value }))} className="border rounded px-3 py-2">
                                    <option value="Not Started">Not Started</option>
                                    <option value="Planning">Planning</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="In Review">In Review</option>
                                    <option value="Blocked">Blocked</option>
                                    <option value="On Hold">On Hold</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Cancelled">Cancelled</option>
                                  </select>
                                </div>
                                <textarea rows={5} value={editDraft?.statusText || ''} onChange={(e) => setEditDraft(prev => ({ ...prev, statusText: e.target.value }))} className="w-full border rounded px-3 py-2" />
                                <div className="flex items-center gap-3">
                                  <button type="button" onClick={() => saveEdit(update)} disabled={savingRowId === update.id} className="px-4 py-2 text-sm bg-primary text-white rounded disabled:opacity-60">{savingRowId === update.id ? 'Saving...' : 'Save'}</button>
                                  <button type="button" onClick={() => { setEditingRowId(null); setEditDraft(null); }} className="px-4 py-2 text-sm border rounded">Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <StatusText text={summary} className="text-gray-700" />
                            )}
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Details</p>
                            {editMeta.canEdit && !isEditing && (
                              <button type="button" onClick={() => startEdit(update)} className="mb-2 text-sm text-blue-700 hover:underline md:hidden">Edit</button>
                            )}
                            <div className="space-y-1 text-gray-700">
                              {showUserColumn && <p><span className="font-medium">User Spent:</span> {update.user?.name || '-'}</p>}
                              <p><span className="font-medium">Hours:</span> {Number(update.timeSpentHours || 0).toFixed(1)}</p>
                              <p><span className="font-medium">Posted:</span> {formatDateTime(getUpdateDateValue(update))}</p>
                              <p><span className="font-medium">Updated Time:</span> {formatTimeOnly(getCreatedAtValue(update))}</p>
                              {(update.task?.targetDate || update.task?.revisedDate) && (
                                <p>
                                  <span className="font-medium">Target / Revised:</span>{' '}
                                  {update.task?.targetDate || '-'}
                                  {update.task?.revisedDate ? ` / ${update.task.revisedDate}` : ''}
                                </p>
                              )}
                              {update.blockers && <p><span className="font-medium">Blockers:</span> {update.blockers}</p>}
                            </div>
                            {!editMeta.canEdit && <p className="text-xs text-gray-500 mt-3">{editMeta.reason}</p>}
                            {externalLink && <a href={externalLink} target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-primary hover:underline">Open external link</a>}
                          </div>
                        </div>
                        {rowMessages[update.id] && (
                          <p className={`mt-3 text-sm ${rowMessages[update.id].includes('successfully') ? 'text-green-700' : 'text-red-600'}`}>{rowMessages[update.id]}</p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="p-10 text-center text-gray-500">No status updates match your filters.</div>
      )}
    </div>
  );
};

export default StatusUpdatesTable;
